import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Briefcase, ExternalLink } from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { endringsLinjer } from "@/components/endringMerke";
import { tooltipStyle } from "./oversiktConstants";
import { ShareTable } from "./ShareCompare";
import { num, dec } from "@/lib/formatters";

const STATUS = [
  { key: "sysselsatte", label: "Sysselsette (i arbeid)", color: "#103766" },
  { key: "arbeidsledige", label: "Registrerte arbeidslause", color: "#D35E4E" },
  { key: "tiltak", label: "På arbeidsmarkedstiltak", color: "#E8A34A" },
  { key: "utdanning", label: "Under utdanning", color: "#3F8A86" },
  { key: "aap_ufore", label: "Arbeidsavklaringspengar / uføretrygd", color: "#8B5CF6" },
  { key: "pensjon", label: "AFP / alderspensjon", color: "#8AA4BF" },
  { key: "andre", label: "Andre utanfor arbeidsstyrken", color: "#C98BDB" },
];
const ARBEIDSTID = [
  { key: "p100", label: "100 % (heiltid)", color: "#103766" },
  { key: "p80_99", label: "80–99 %", color: "#3F8A86" },
  { key: "p60_79", label: "60–79 %", color: "#E8A34A" },
  { key: "p40_59", label: "40–59 %", color: "#D35E4E" },
  { key: "p20_39", label: "20–39 %", color: "#8B5CF6" },
  { key: "p0_19", label: "0–19 %", color: "#8AA4BF" },
];

const latestOf = (rows) => (rows || [])[rows?.length - 1];

const StackedRegions = ({ fields, section, kommune, fylke, land, fylkeName, testId }) => {
  const regions = [["Kommunen", kommune], [fylkeName ? `Snitt ${fylkeName}` : "Fylket", fylke], ["Landet", land]].filter(([, r]) => r?.[section]);
  const data = regions.map(([name, r]) => ({ name, ...Object.fromEntries(fields.map((f) => [f.label, r[section][f.key] ?? 0])) }));
  return (
    <div className="h-[200px]" data-testid={testId}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" stackOffset="expand" barCategoryGap="30%">
          <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
          <XAxis type="number" tickFormatter={(v) => `${Math.round(v * 100)} %`} stroke="#8AA4BF" fontSize={12} />
          <YAxis type="category" dataKey="name" width={130} stroke="#8AA4BF" fontSize={12} />
          <Tooltip formatter={(v) => `${num(v)} %`} contentStyle={tooltipStyle} />
          <Legend wrapperStyle={{ fontSize: 11 }} itemSorter={null} />
          {fields.map((f) => <Bar key={f.key} dataKey={f.label} stackId="s" fill={f.color} />)}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const toShareRows = (fields, section, k, f, l) =>
  fields.map((x) => ({ key: x.key, name: x.label, color: x.color, share: k?.[section]?.[x.key] ?? 0, fylkeShare: f?.[section]?.[x.key] ?? null, landShare: l?.[section]?.[x.key] ?? null }))
    .filter((r) => r.share > 0 || r.landShare > 0);

export const ArbeidSection = ({ data }) => {
  const k = latestOf(data?.kommune);
  if (!k) return null;
  const f = (data.fylke || []).find((r) => r.year === k.year);
  const l = (data.land || []).find((r) => r.year === k.year);
  const fylkeName = data.fylke_name;
  const trend = (data.kommune || []).map((r) => ({
    year: r.year,
    Kommunen: r.status_pct?.sysselsatte,
    ...(data.fylke?.length ? { [`Snitt ${fylkeName}`]: data.fylke.find((x) => x.year === r.year)?.status_pct?.sysselsatte } : {}),
    Landet: (data.land || []).find((x) => x.year === r.year)?.status_pct?.sysselsatte,
  }));

  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8" data-testid="arbeid-section" id="arbeid">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-[#103766]" />
          Arbeid, arbeidsløyse og ytingar {k.year}
        </CardTitle>
        <p className="text-sm text-[#4B647F]">Busette 20–66 år etter prioritert arbeidsstyrkestatus, og stillingsprosent blant sysselsette 15–74 år (bustad)</p>
      </CardHeader>
      <CardContent className="pt-6 space-y-8">
        <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
          <div className="xl:col-span-3">
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">Arbeidsstyrkestatus, andel av busette 20–66 år</p>
            <StackedRegions fields={STATUS} section="status_pct" kommune={k} fylke={f} land={l} fylkeName={fylkeName} testId="arbeid-status-chart" />
            <p className="text-3xl font-light text-[#1F2A36] mt-4" data-testid="arbeid-sysselsatte">{num(k.status_pct?.sysselsatte)} % <span className="text-sm text-[#4B647F]">i arbeid</span></p>
            <p className="text-sm text-[#4B647F]"><span data-testid="arbeid-ledige">{num(k.status_pct?.arbeidsledige)} %</span> registrerte arbeidslause · <span data-testid="arbeid-aap-ufore">{num(k.status_pct?.aap_ufore)} %</span> på AAP/uføretrygd · landet {num(l?.status_pct?.sysselsatte)} % i arbeid</p>
          </div>
          <div className="xl:col-span-2">
            <ShareTable rows={toShareRows(STATUS, "status_pct", k, f, l)} hasFylke={!!f} hasLand={!!l} testId="arbeid-status" />
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">Andel i arbeid over tid (%)</p>
          <div className="h-[240px]" data-testid="arbeid-trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend}>
                {endringsLinjer(trend)}
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
                <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} />
                <YAxis stroke="#8AA4BF" fontSize={12} domain={["auto", "auto"]} tickFormatter={(v) => `${num(v)} %`} />
                <Tooltip formatter={(v) => `${num(v)} %`} contentStyle={tooltipStyle} />
                <Legend />
                <Line type="monotone" dataKey="Kommunen" stroke="#103766" strokeWidth={3} dot={{ r: 3 }} connectNulls />
                {data.fylke?.length > 0 && (
                  <Line type="monotone" dataKey={`Snitt ${fylkeName}`} stroke="#8AA4BF" strokeWidth={2} strokeDasharray="5 5" dot={false} connectNulls />
                )}
                <Line type="monotone" dataKey="Landet" stroke="#E8A34A" strokeWidth={2} strokeDasharray="5 5" dot={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
          <div className="xl:col-span-3">
            <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#8AA4BF] mb-2">Stillingsprosent blant sysselsette (andel)</p>
            <StackedRegions fields={ARBEIDSTID} section="arbeidstid_pct" kommune={k} fylke={f} land={l} fylkeName={fylkeName} testId="arbeid-arbeidstid-chart" />
            <p className="text-3xl font-light text-[#1F2A36] mt-4" data-testid="arbeid-heltid">{num(k.arbeidstid_pct?.p100)} % <span className="text-sm text-[#4B647F]">jobbar 100 % eller meir</span></p>
            <p className="text-sm text-[#4B647F]">Landet {dec(l?.arbeidstid_pct?.p100, 1)} %{f ? ` · ${fylkeName} ${dec(f.arbeidstid_pct?.p100, 1)} %` : ""}</p>
          </div>
          <div className="xl:col-span-2">
            <ShareTable rows={toShareRows(ARBEIDSTID, "arbeidstid_pct", k, f, l)} hasFylke={!!f} hasLand={!!l} testId="arbeid-arbeidstid" />
          </div>
        </div>

        <p className="text-xs text-[#8AA4BF]">
          Kjelder: SSB{" "}
          <a href="https://www.ssb.no/statbank/table/13563" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">13563 <ExternalLink className="w-3 h-3" /></a>{" "}
          (prioritert arbeidsstyrkestatus, 4. kvartal) og{" "}
          <a href="https://www.ssb.no/statbank/table/12540" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">12540 <ExternalLink className="w-3 h-3" /></a>{" "}
          (sysselsette etter avtalt stillingsprosent). «Prioritert status» tyder at kvar person blir rekna i éin kategori: arbeid går føre utdanning, som går føre ytingar.
        </p>
      </CardContent>
    </Card>
  );
};

export default ArbeidSection;
